import React, { useEffect, useState } from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { formatCallToAction } from 'components/ad-preview/local/formatCallToAction'
import { accentSoft, radius, SurfacePalette, type as typo } from '../tokens'

interface Props {
  creative: AdCreative
  palette: SurfacePalette
}

/**
 * One entry in the unified media list (images then videos). The main stage and
 * the thumbnail strip both render from this so selection stays index-stable.
 */
type MediaItem =
  | { kind: 'image'; key: string; url: string; alt?: string }
  | {
      kind: 'video'
      key: string
      thumbnail?: string
      permalinkUrl: string
      title?: string
      /** Fresh signed URL to our durable S3 copy; lets the stage play inline without Facebook. */
      storedSource?: string
      /** Expiring Facebook source; second choice when no durable copy exists. */
      source?: string
    }

/** Eyebrow label above a stacked group of text variants. */
const GroupLabel = ({
  palette,
  children,
}: {
  palette: SurfacePalette
  children: React.ReactNode
}) => (
  <span
    style={{
      display: 'block',
      fontFamily: typo.family.sans,
      fontSize: typo.size.eyebrow,
      fontWeight: typo.weight.semibold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: palette.textFaint,
      marginBottom: 6,
    }}
  >
    {children}
  </span>
)

/** A labeled group of stacked text variants; renders nothing when empty. */
const TextGroup = ({
  palette,
  label,
  items,
}: {
  palette: SurfacePalette
  label: string
  items?: string[]
}) => {
  if (!items || items.length === 0) return null
  return (
    <div>
      <GroupLabel palette={palette}>{label}</GroupLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, i) => (
          <div
            key={`${item}-${i}`}
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              lineHeight: 1.4,
              color: palette.text,
              background: palette.bg,
              border: `1px solid ${palette.hairline}`,
              borderRadius: radius.sm,
              padding: '8px 10px',
            }}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  )
}

/** Centered play badge used on video posters (main stage + strip thumbs). */
const PlayBadge = ({ size }: { size: number }) => (
  <span
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
    }}
  >
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'rgba(0, 0, 0, 0.55)',
        color: '#fff',
      }}
    >
      <i className="fa fa-play" style={{ fontSize: size * 0.36, marginLeft: 2 }} />
    </span>
  </span>
)

/**
 * The large inspect-quality view of the selected asset. Images render `contain`
 * so nothing is cropped. Videos render their poster + a play badge and click out
 * to `permalinkUrl` — we deliberately do NOT embed the live FB player here (the
 * dynamic view exists to bypass the FB iframe; a poster + click-out is never-blank
 * and network-light — see the v1.5a handoff).
 */
const MainStage = ({ palette, item }: { palette: SurfacePalette; item: MediaItem }) => {
  const frame: React.CSSProperties = {
    flex: 1,
    minHeight: 280,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: palette.bg,
    border: `1px solid ${palette.hairline}`,
    borderRadius: radius.sm,
  }
  const fit: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
  }

  if (item.kind === 'image') {
    return (
      <div style={frame}>
        <img src={item.url} alt={item.alt} style={fit} />
      </div>
    )
  }

  // Keyed by asset so switching videos in the strip resets the inline-player error state.
  return <VideoStage key={item.key} palette={palette} item={item} frame={frame} fit={fit} />
}

/**
 * Video main stage. When we have a playable source (our durable stored copy first,
 * the expiring Facebook source second) it plays inline — the whole point of the
 * feature, so a reviewer can watch without a working Facebook session. If that
 * source fails to load, or none exists, it degrades to the original poster + play
 * badge that clicks out to Facebook (network-light and never blank).
 */
const VideoStage = ({
  palette,
  item,
  frame,
  fit,
}: {
  palette: SurfacePalette
  item: Extract<MediaItem, { kind: 'video' }>
  frame: React.CSSProperties
  fit: React.CSSProperties
}) => {
  const [errored, setErrored] = useState(false)
  const [posterErrored, setPosterErrored] = useState(false)
  const source = item.storedSource ?? item.source

  if (source && !errored) {
    return (
      <div style={frame}>
        <video
          src={source}
          poster={item.thumbnail}
          controls
          playsInline
          preload="metadata"
          onError={() => setErrored(true)}
          style={fit}
        />
      </div>
    )
  }

  return (
    <a
      href={item.permalinkUrl}
      target="_blank"
      rel="noopener noreferrer"
      title={item.title ? `Play “${item.title}”` : 'Play video'}
      style={{ ...frame, textDecoration: 'none' }}
    >
      {item.thumbnail && !posterErrored ? (
        <img
          src={item.thumbnail}
          alt={item.title || 'video thumbnail'}
          onError={() => setPosterErrored(true)}
          style={fit}
        />
      ) : (
        <span
          style={{
            color: palette.textFaint,
            fontSize: typo.size.body,
            fontFamily: typo.family.sans,
            textAlign: 'center',
            padding: 16,
          }}
        >
          No thumbnail — open to play
        </span>
      )}
      <PlayBadge size={56} />
    </a>
  )
}

/** A small selectable strip thumbnail. Selecting promotes the item to the stage. */
const StripThumb = ({
  palette,
  item,
  selected,
  onSelect,
}: {
  palette: SurfacePalette
  item: MediaItem
  selected: boolean
  onSelect: () => void
}) => {
  const [thumbErrored, setThumbErrored] = useState(false)
  const rawSrc = item.kind === 'image' ? item.url : item.thumbnail
  // Facebook thumbnail URLs expire; a failed load falls back to the film-icon
  // placeholder rather than a broken-image glyph.
  const src = thumbErrored ? undefined : rawSrc
  const label =
    item.kind === 'image' ? 'View image' : item.title ? `View video: ${item.title}` : 'View video'
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={label}
      aria-pressed={selected}
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 64,
        height: 64,
        padding: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        background: palette.bg,
        borderRadius: radius.sm,
        border: `2px solid ${selected ? palette.accent : palette.hairline}`,
      }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          onError={() => setThumbErrored(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: palette.textFaint,
            fontSize: typo.size.eyebrow,
          }}
        >
          <i className="fa fa-film" />
        </span>
      )}
      {item.kind === 'video' && <PlayBadge size={22} />}
    </button>
  )
}

/**
 * Grouped, presentational view of a dynamic creative's full asset pool, laid out
 * as two panes: a media pane (a large main stage + a thumbnail strip of the whole
 * set) on the left, and a scrollable text-variant comparison column on the right.
 * It shows everything Facebook will mix and match — this is NOT a per-permutation
 * preview and NOT a single combination; it deliberately bypasses the live FB
 * iframe (and its permutation "roulette") and renders straight from the hydrated
 * `dynamicConfig` / `dynamicImages` / `dynamicVideos` fields.
 *
 * Media gets the larger pane and click-through (you judge each asset on its own);
 * text is a single scrollable list so variants can be compared at once. Approval
 * is creative-level via the modal rail (it approves the whole set); per-asset
 * feedback goes through the existing creative comment thread. Reads on both the
 * light (Edit) and dark (Present) palettes since the modal hosts it in both.
 */
export function DynamicAssetPool({ creative, palette }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)
  const dynamic = isDynamicCreative(creative) ? creative : undefined

  const media: MediaItem[] = dynamic
    ? [
        ...dynamic.dynamicImages.map(
          (image): MediaItem => ({
            kind: 'image',
            key: `img-${image.id}`,
            url: image.url,
            alt: image.name,
          })
        ),
        ...dynamic.dynamicVideos.map(
          (video): MediaItem => ({
            kind: 'video',
            key: `vid-${video.id}`,
            thumbnail: video.thumbnail,
            permalinkUrl: video.permalinkUrl,
            title: video.title,
            storedSource: video.storedSource,
            source: video.source,
          })
        ),
      ]
    : []
  const mediaCount = media.length

  // Left/Right arrows cycle the active media item, so a reviewer can flip through
  // the pool without reaching for the strip. Ignored while typing in a field (e.g.
  // the comment box) so it doesn't hijack cursor movement.
  useEffect(() => {
    if (mediaCount < 2) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      if (e.key === 'ArrowRight') setActiveIndex(i => (i + 1) % mediaCount)
      else if (e.key === 'ArrowLeft') setActiveIndex(i => (i - 1 + mediaCount) % mediaCount)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mediaCount])

  if (!dynamic) return null
  const { dynamicConfig } = dynamic
  const ctas = dynamicConfig.callToActions?.map(formatCallToAction)
  // Guard against a stale index if the pool refetches to fewer assets while open.
  const active = media[activeIndex] ?? media[0]

  const textColumn = (
    <>
      <TextGroup palette={palette} label="Primary text" items={dynamicConfig.primaryTexts} />
      <TextGroup palette={palette} label="Headlines" items={dynamicConfig.headlines} />
      <TextGroup palette={palette} label="Descriptions" items={dynamicConfig.descriptions} />
      <TextGroup palette={palette} label="Call to action" items={ctas} />
    </>
  )

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        textAlign: 'left',
      }}
    >
      {/* Explainer banner — pinned above both panes; frames the whole set. */}
      <div
        style={{
          flexShrink: 0,
          background: accentSoft,
          border: `1px solid ${palette.accent}`,
          borderRadius: radius.sm,
          padding: '10px 12px',
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: typo.family.sans,
            fontSize: typo.size.body,
            lineHeight: 1.45,
            color: palette.text,
          }}
        >
          Facebook automatically mixes and matches these assets to find the best-performing
          combinations. Approving accepts the full set below.
        </p>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16 }}>
        {/* Media pane (~60%): big main stage + a strip of the full set. */}
        {active && (
          <div
            style={{
              flex: '3 1 0',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <MainStage palette={palette} item={active} />
            {media.length > 1 && (
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  gap: 8,
                  overflowX: 'auto',
                  paddingBottom: 4,
                }}
              >
                {media.map((item, i) => (
                  <StripThumb
                    key={item.key}
                    palette={palette}
                    item={item}
                    selected={item.key === active.key}
                    onSelect={() => setActiveIndex(i)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Text pane (~40%): scrollable variant comparison list. */}
        <div
          style={{
            flex: active ? '2 1 0' : '1 1 0',
            minWidth: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {textColumn}
        </div>
      </div>
    </div>
  )
}
